<template>
	<Modal v-if="shown"
		   :title= "$t('investor.exchange.modal.alfa.title')"
		   @close="confirmClose"
		   :close-on-click-outside="false"
		   :width="1000"
	>
		<template v-slot:body>
			<p class="modal-content-pre-group-description text_left font_bold">
				{{ $t('investor.exchange.modal.alfa.description') }}
			</p>
			<!-- Tab buttons -->
			<div class="flex flex_between md-group">
				<md-button class="flex_1"
						   :class="{'md-primary': activeTabKey === tabs.targetsAndLimits.key }"
						   @click="changeActiveTab(tabs.targetsAndLimits.key)">
					{{ tabs.targetsAndLimits.name }}
				</md-button>
				<md-button class="flex_1"
						   :class="{'md-primary': activeTabKey === tabs.receivableConstraints.key }"
						   @click="changeActiveTab(tabs.receivableConstraints.key)">
					{{ tabs.receivableConstraints.name }}
				</md-button>
				<md-button class="flex_1"
						   :class="{'md-primary': activeTabKey === tabs.portfolioDiversification.key }"
						   @click="changeActiveTab(tabs.portfolioDiversification.key)">
					{{ tabs.portfolioDiversification.name }}
				</md-button>
			</div>
			<div class="flex flex_column flex_center">
				<div class="w_90">
					<div class="modal-content modal-content--in-group-with-description flex margin_top_l">
						<!-- Tab contents -->
						<div class="w_100 h_100">
							<AlfaTargetsAndLimits v-show="activeTabKey === tabs.targetsAndLimits.key"
												  :data="settings.targetsAndLimits"
												  :currency="account.tbCurrType"
												  :tci-confirmation-status="tciConfirmationStatus"
												  ref="targetsTab"
							/>
							<template v-if="alfaConfigLoaded">
								<AlfaReceivableConstraints v-show="activeTabKey === tabs.receivableConstraints.key"
														   :data="settings.receivableConstraints"
														   ref="constraintsTab"
								/>
								<AlfaPortfolioDiversification v-show="activeTabKey === tabs.portfolioDiversification.key"
															  :data="settings.portfolioDiversification"
															  ref="limitsTab"
								/>
							</template>
						</div>
						<!-- Coverage display -->
						<template v-if="alfaConfig.showCoverage">
							<div v-show="activeTab.includeInCoverageCheck" class="margin_horizontal" style="max-width: 175px; position: sticky; top: 0;">
								<span class="font_sm secondary_color">
									{{ $t("investor.exchange.modal.alfa.coverage.description", { days: coverageDays }) }}
								</span>
								<el-tooltip :open-delay="300" placement="bottom"
											:content="$t('investor.exchange.modal.alfa.coverage.tooltip')">
									<i class="fa fa-info-circle secondary_color" aria-hidden="true"/>
								</el-tooltip>
								<AlfaCoverageDisplay class="margin_top"
													 :needs-update="coverageNeedsUpdate" :ratio="coverage.countRatio"
													 :description="$t('investor.exchange.modal.alfa.coverage.count.description')"
													 :tooltip="$t('investor.exchange.modal.alfa.coverage.count.tooltip', { days: coverageDays })"
													 @update="updateCoverage"
								/>
								<AlfaCoverageDisplay class="margin_top"
													 :needs-update="coverageNeedsUpdate" :ratio="coverage.volumeRatio"
													 :description="$t('investor.exchange.modal.alfa.coverage.volume.description')"
													 :tooltip="$t('investor.exchange.modal.alfa.coverage.volume.tooltip', { days: coverageDays })"
													 @update="updateCoverage"
								/>
							</div>
						</template>
					</div>
					<!-- Modal Buttons -->
					<div class="flex flex_between margin_top">
						<div>
							<md-button class="md-block margin_r" @click="confirmClose">
								{{ $t("common.button.cancel") }}
							</md-button>
							<md-button v-if="activeTabKey !== tabs.targetsAndLimits.key" class="md-blank margin_r" @click="previousTab">
								{{ $t("common.button.back") }}
							</md-button>
						</div>
						<div class="flex">
							<md-button v-if="activeTabKey !== tabs.portfolioDiversification.key" class="md-blank margin_r" @click="nextTab">
								{{ $t("common.button.next") }}
							</md-button>
							<md-button v-if="!areSettingsActive" class="md-primary margin_r" @click="saveSettings(false)">
								{{ $t("common.save") }}
							</md-button>
							<md-button class="md-success" @click="confirmSaveAndStart">
								<i class="fa fa-play" style="top: 1px;"></i>
								{{ $t("investor.exchange.modal.alfa.button.saveAndEnableAlfa") }}
							</md-button>
						</div>
					</div>
				</div>
			</div>
		</template>
	</Modal>
</template>

<script>
import Modal from "@/components/templates/modal/Modal";
import AlfaTargetsAndLimits from "@/components/templates/investor/exchange/alfa/AlfaTargetsAndLimits";
import AlfaReceivableConstraints from "@/components/templates/investor/exchange/alfa/AlfaReceivableConstraints";
import AlfaPortfolioDiversification from "@/components/templates/investor/exchange/alfa/AlfaPortfolioDiversification";
import {alfaApi} from "@/common/api/alfaApi";
import {mapActions, mapGetters} from "vuex";
import {enumDef} from "@/common/enumDefs";
import AlfaCoverageDisplay from "@/components/templates/investor/exchange/alfa/AlfaCoverageDisplay.vue";

export default {
	components: {
		AlfaCoverageDisplay,
		AlfaPortfolioDiversification,
		AlfaReceivableConstraints,
		AlfaTargetsAndLimits,
		Modal
	},
	props: {
		account: { type: Object, default: null },
		tciConfirmationStatus: { type: Number, default: enumDef.creditInsurancePolicyHolderStatus.NONE }
	},
	data() {
		let tabs = {
			targetsAndLimits: {
				key: "targetsAndLimits",
				name: this.$t("investor.exchange.modal.alfa.tab.targetsAndLimits"),
				includeInCoverageCheck: true,
				validation: () => this.$refs.targetsTab.validate(),
			},
			receivableConstraints: {
				key: "receivableConstraints",
				name: this.$t("investor.exchange.modal.alfa.tab.receivableConstraints"),
				includeInCoverageCheck: true,
				validation: () => this.$refs.constraintsTab.validate()
			},
			portfolioDiversification: {
				key: "portfolioDiversification",
				name: this.$t("investor.exchange.modal.alfa.tab.portfolioDiversification"),
				includeInCoverageCheck: false,
				validation: () => this.$refs.limitsTab.validate()
			}
		};

		let tabKeysInOrder = [
			tabs.targetsAndLimits.key,
			tabs.receivableConstraints.key,
			tabs.portfolioDiversification.key
		];

		let emptySettings = {
			id: null,
			accountId: null,
			status: null,
			targetsAndLimits: {
				totalInvestmentVolume: null,
				usedInvestmentVolume: 0,
				tciPreference: enumDef.alfaBuyPreference.Never, // TCI is disabled, so we enforce NEVER
				retentionPreference: null,
				netTargetReturnRatePerAnnumRiskLow: null,
				netTargetReturnRatePerAnnumRiskMedium: null,
				netTargetReturnRatePerAnnumRiskHigh: null,
				netTargetReturnRatePerAnnumRiskUnrated: null,
				targetReturnRateAddReferenceInterestRateWithoutTci: false,
				netTargetReturnRatePerAnnumWithTci: null,
				targetReturnRateAddReferenceInterestRateWithTci: false,
				targetReturnRateAddFees: true, // Set by default and cannot be changed
				targetReturnRateAddInsurancePremium: true, // Set by default and cannot be changed
			},
			receivableConstraints: {
				receivableConditions: []
			},
			portfolioDiversification: {
				generalLimits: [],
				specialLimitGroups: []
			}
		}

		let defaultCoverage = {
			countRatio: null,
			volumeRatio: null
		}

		return {
			shown: false,
			tabs: tabs,
			activeTabKey: tabKeysInOrder[0],
			tabKeysInOrder: tabKeysInOrder,
			emptySettings: emptySettings,
			settings: this.buildNewEmptySettings(),
			config: null,
			checkForChanges: true,
			hasChanges: false,
			coverageDays: 30,
			defaultCoverage: defaultCoverage,
			coverage: {...defaultCoverage},
			coverageNeedsUpdate: true
		};
	},
	computed: {
		...mapGetters({
			alfaConfigLoaded: "alfa/configLoaded",
			alfaConfig: "alfa/config"
		}),
		areSettingsActive() {
			return this.settings.status === enumDef.alfaSettingsStatus.Active;
		},
		activeTabIndex() {
			return this.tabKeysInOrder.indexOf(this.activeTabKey);
		},
		activeTab() {
			return this.tabs[this.activeTabKey];
		}
	},
	methods: {
		...mapActions({
			setAlfaConfig: "alfa/setActionConfig"
		}),
		show(value) {
			this.shown = value;

			if (!value) {
				this.$emit("close");
			}
		},
		confirmClose() {
			if (this.hasChanges) {
				this.showHasChangesCloseConfirmation({
					onSave: () => this.areSettingsActive ? this.saveAndStart(true) : this.saveSettings(true), // Save (without additional confirm) and then close),
					onNotSave: () => this.show(false), // Simply close window without saving
					saveButtonClass: this.areSettingsActive ? 'md-success' : 'md-primary',
					saveButtonText: this.areSettingsActive ? this.$t("investor.exchange.modal.alfa.button.saveAndEnableAlfa") : this.$t("common.save")
				});
			} else {
				this.show(false);
			}
		},
		showForSettings(settingsId) {
			if (!this.alfaConfigLoaded) {
				this.prepareConfig();
			}

			let afterSettingsLoaded = (settings) => {
				this.updateWithNewSettings(settings);
				this.resetCoverage();
				this.changeActiveTab(this.tabKeysInOrder[0]);
				this.show(true);
			};

			if (settingsId) {
				alfaApi.inspection.getDetails(settingsId)
					.then(afterSettingsLoaded)
					.catch(this.toast);
			} else {
				afterSettingsLoaded(this.buildNewEmptySettings());
			}
		},
		changeActiveTab(tabKey) {
			this.activeTabKey = tabKey;
		},
		prepareConfig() {
			alfaApi.inspection.getConfig()
				.then(this.setAlfaConfig)
				.catch(this.toast);
		},
		validateTab(tab) {
			return new Promise((resolve, reject) => {
				tab.validation()
					.then(() => resolve())
					.catch((msg) => {
						this.toast(tab.name + ": " + msg);
						reject();
					});
			});
		},
		validate(forCoverageCheck = false) {
			return Promise.all(Object.keys(this.tabs)
					.filter(tabKey => !forCoverageCheck || this.tabs[tabKey].includeInCoverageCheck)
					.map(tabKey => this.validateTab(this.tabs[tabKey]))
			);
		},
		nextTab() {
			this.validateTab(this.activeTab)
				.then(() => this.changeActiveTab(this.tabKeysInOrder[this.activeTabIndex + 1]))
				.catch(() => {});
		},
		previousTab() {
			this.changeActiveTab(this.tabKeysInOrder[this.activeTabIndex - 1]);
		},
		saveSettings(closeAfterAction) {
			this.validate()
				.then(() => {
					alfaApi.operation.save(this.settings, false)
						.then(this.updateWithNewSettings)
						.then(() => this.afterUpdate(closeAfterAction))
						.catch(this.toast);
				})
				.catch(() => {});
		},
		confirmSaveAndStart() {
			this.validate()
				.then(() => {
					let option = {
						title: this.$t("investor.exchange.alfa.start.confirm.title"),
						msg: this.buildHintBoxForConfirmation(this.$t("investor.exchange.alfa.start.confirm.msg", { stopButtonText: this.$t("investor.exchange.button.stopAlfa") }), false),
						confirmButtonText: this.$t("investor.exchange.modal.alfa.button.saveAndEnableAlfa"),
						confirmButtonClass: "md-success",
						customClass: "msg_box_title_bold msg_box_width_l",
						confirmFun: () => this.saveAndStart(true),
					};
					this.confirmMeg(option, true);
				})
				.catch(() => {});
		},
		saveAndStart(closeAfterAction) {
			if (this.hasChanges) {
				this.validate()
					.then(() => {
						alfaApi.operation.save(this.settings, !this.areSettingsActive)
							.then(this.updateWithNewSettings)
							.then(() => this.afterUpdate(closeAfterAction))
							.catch(this.toast);
					})
					.catch(() => {});
			} else if (!this.areSettingsActive) {
				alfaApi.operation.enable(this.settings.id)
					.then(() => this.updateStatus(enumDef.alfaSettingsStatus.Active))
					.then(() => this.afterUpdate(closeAfterAction))
					.catch(this.toast);
			} else {
				this.afterUpdate(closeAfterAction);
			}
		},
		updateWithNewSettings(newSettings) {
			this.checkForChanges = false;
			this.hasChanges = false;
			
			this.settings = newSettings;

			this.$nextTick(() => this.checkForChanges = true);
		},
		updateStatus(newStatus) {
			this.checkForChanges = false;

			this.settings.status = newStatus;

			this.$nextTick(() => this.checkForChanges = true);
		},
		afterUpdate(close) {
			this.toastSuccess(this.$t("common.saved.success"));

			if (close) {
				this.show(false);
			}
		},
		buildNewEmptySettings() {
			let newEmptySettings = _.cloneDeep(this.emptySettings);

			if (this.account) {
				newEmptySettings.accountId = this.account.id;
			}

			return newEmptySettings;
		},
		updateCoverage() {
			if (!this.coverageNeedsUpdate) {
				return;
			}

			this.validate()
				.then(this.loadCoverage)
				.catch(this.toast);
		},
		loadCoverage() {
			let settingsForCoverageCheck = {
				accountId: this.settings.accountId,
				targetsAndLimits: this.buildForCoverageCheck(this.settings.targetsAndLimits, this.tabs.targetsAndLimits.includeInCoverageCheck),
				receivableConstraints: this.buildForCoverageCheck(this.settings.receivableConstraints, this.tabs.receivableConstraints.includeInCoverageCheck),
				portfolioDiversification: this.buildForCoverageCheck(this.settings.portfolioDiversification, this.tabs.portfolioDiversification.includeInCoverageCheck)
			};

			alfaApi.inspection.getCoverage(settingsForCoverageCheck, this.coverageDays)
				.then((coverage) => {
					this.coverage = coverage;
					this.coverageNeedsUpdate = false;
				})
				.catch(this.toast);
		},
		buildForCoverageCheck(data, includeInCoverageCheck) {
			return includeInCoverageCheck ? data : {};
		},
		resetCoverage() {
			this.coverage = { ...this.defaultCoverage };
			this.coverageNeedsUpdate = true;
		}
	},
	watch: {
		settings: {
			handler() {
				if (this.checkForChanges) {
					this.hasChanges = true;

					this.resetCoverage();
				}
			},
			deep: true
		}
	}
};
</script>